import type { Latam3DSConfig, Latam3DSData, Latam3DSCallbacks } from './latam.3ds.types';

// Declaração global para o objeto Latam
declare global {
  interface Window {
    Latam?: {
      Environment: {
        HOMOLOG: string; // ✅ Environment correto da documentação
        PRODUCTION: string; // ✅ Environment correto da documentação
      };
      ThreeDSecure: new (config: any) => any;
    };
    BT?: any;
    Braintree?: any;
    LatamGateway?: any;
  }
}

/**
 * Script para integração com JavaScript 3DS da Latam
 */
export class Latam3DSScript {
  private static scriptLoaded = false;
  private static scriptLoading = false;
  private static maxRetries = 3;
  private static retryCount = 0;
  private static scriptElement: HTMLScriptElement | null = null;
  private static observer: MutationObserver | null = null;
  private static observerAttached = false;

  // Resolve o objeto Latam mesmo quando definido como binding global (não anexado ao window)
  private static resolveLatamObject(): any {
    const win = window as any;
    if (win && win.Latam) return win.Latam;
    // Alguns builds expõem como LatamGateway
    if (win && win.LatamGateway) return win.LatamGateway;
    try {
      // Indirect eval para acessar binding global criado por "const Latam = {...}"
      const globalLatam = (0, eval)('Latam');
      if (globalLatam) return globalLatam;
    } catch {}
    try {
      // Fallback adicional usando Function (contorna diferenças de modo estrito)
      const fnLatam = Function('try { return Latam } catch(e) { return null }')();
      if (fnLatam) return fnLatam;
    } catch {}
    try {
      const gt = globalThis as any;
      if (gt && gt.Latam) return gt.Latam;
      if (gt && gt.LatamGateway) return gt.LatamGateway;
    } catch {}
    return null;
  }

  static async loadScript(): Promise<boolean> {
    if (this.scriptLoaded) {
      console.log('✅ Script já carregado');
      return true;
    }

    if (this.scriptLoading) {
      console.log('⏳ Script já está carregando...');
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (this.scriptLoaded) {
            clearInterval(checkInterval);
            resolve(true);
          }
        }, 100);
      });
    }

    // ✅ LOG BACKEND: Tentando carregar script 3DS
    this.logToBackend('Carregando script 3DS', 'loading');

    // Verifica se já existe um script na página
    const existingScript = document.querySelector('script[src*="bt-3d-secure.min.js"]');
    if (existingScript) {
      console.log('✅ Script já existe na página, aguardando carregamento...');
      this.scriptLoading = true;

      // Aguarda um pouco e verifica se o objeto foi criado
      setTimeout(() => {
        this.verifyScriptLoaded(() => {
          this.scriptLoading = false;
        });
      }, 1000);

      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (this.scriptLoaded) {
            clearInterval(checkInterval);
            resolve(true);
          }
        }, 100);
      });
    }

    this.scriptLoading = true;

    // Observa scripts internos injetados (ex.: Braintree) após o carregamento do bt-3d-secure
    this.setupBraintreeScriptObserver();

    return new Promise((resolve) => {
      // ✅ URL correta baseada na documentação oficial
      //const scriptUrl = 'https://sulpayments.ch/javascripts/bt-3d-secure.min.js';
      const scriptUrl = 'https://payin.sulpayments.ch/javascripts/bt-3d-secure.min.js';

      this.scriptElement = document.createElement('script');
      this.scriptElement.src = scriptUrl;
      this.scriptElement.async = true;

      this.scriptElement.onload = () => {
        // Aguarda o script inicializar completamente
        setTimeout(() => {
          this.verifyScriptLoaded(resolve);
        }, 2000);
      };

      this.scriptElement.onerror = () => {
        console.error('❌ Erro ao carregar script:', scriptUrl);
        console.log('🔄 Tentando URL alternativa...');

        // ✅ URL alternativa (homologação) baseada na documentação
        const fallbackUrl = 'https://homolog.sulpayments.ch/javascripts/bt-3d-secure.min.js';
        const fallbackScript = document.createElement('script');
        fallbackScript.src = fallbackUrl;
        fallbackScript.async = true;

        fallbackScript.onload = () => {
          console.log('✅ Script carregado com sucesso (fallback):', fallbackUrl);
          setTimeout(() => {
            this.verifyScriptLoaded(resolve);
          }, 2000);
        };

        fallbackScript.onerror = () => {
          console.error('❌ Todas as URLs do script falharam');
          this.scriptLoading = false;
          resolve(false); // Retorna false em vez de true
        };

        document.head.appendChild(fallbackScript);
      };

      document.head.appendChild(this.scriptElement);
    });
  }

  private static verifyScriptLoaded(resolve: (value: boolean) => void): void {
    console.log('🔍 Verificando carregamento do script...');

    // Loga presença do braintree quando aparecer
    const w: any = window as any;
    if (w && w.braintree) {
      try {
        const keys = Object.keys(w.braintree || {});
        console.log('✅ braintree detectado no window com chaves:', keys);
      } catch {
        console.log('✅ braintree detectado no window');
      }
    }

    // Verifica se o objeto Latam foi carregado corretamente
    const latamObject = this.resolveLatamObject();
    if (latamObject) {
      console.log('✅ Objeto Latam encontrado:', latamObject);
      console.log('🔍 Estrutura do objeto Latam:', Object.keys(latamObject));

      // Verifica se tem a estrutura esperada baseada na documentação
      if (latamObject.ThreeDSecure && latamObject.Environment) {
        console.log('✅ Objeto Latam tem estrutura válida para 3DS');
        
        // ✅ LOG BACKEND: Script carregou com sucesso
        this.logToBackend('Script 3DS carregado com sucesso', 'success');
        
        this.scriptLoaded = true;
        this.scriptLoading = false;
        this.retryCount = 0;
        // Desconecta observer após sucesso
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
          this.observerAttached = false;
        }
        resolve(true);
        return;
      } else {
        console.log('⚠️ Objeto Latam encontrado mas sem estrutura válida');
        console.log('🔍 ThreeDSecure:', latamObject.ThreeDSecure);
        console.log('🔍 Environment:', latamObject.Environment);
      }
    }

    // Se não encontrou ou não tem estrutura válida, tenta novamente
    this.retryCount++;

    if (this.retryCount >= this.maxRetries) {
      console.log('❌ Máximo de tentativas atingido');
      
      // ✅ LOG BACKEND: Falha ao carregar script 3DS
      this.logToBackend('Falha ao carregar script 3DS - máximo de tentativas atingido', 'error');
      
      this.scriptLoading = false;
      resolve(false); // Retorna false em vez de true
      return;
    }

    console.log(`🔄 Tentativa ${this.retryCount}/${this.maxRetries}, tentando novamente em 1.5s...`);

    // Tenta carregar novamente após um delay
    setTimeout(() => {
      this.verifyScriptLoaded(resolve);
    }, 1500);
  }

  private static setupBraintreeScriptObserver(): void {
    if (this.observerAttached) return;
    try {
      this.observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach((node) => {
              if (node instanceof HTMLScriptElement) {
                const src = node.getAttribute('src') || '';
                if (src.includes('js.braintreegateway.com')) {
                  console.log('🧩 3DS - Script interno injetado:', src);
                  node.addEventListener('load', () => {
                    console.log('🧩 3DS - Script interno carregado:', src);
                  });
                  node.addEventListener('error', () => {
                    console.error('🧩 3DS - Erro ao carregar script interno:', src);
                  });
                }
              }
            });
          }
        }
      });
      this.observer.observe(document.documentElement || document.body, {
        childList: true,
        subtree: true,
      });
      this.observerAttached = true;
      console.log('🧩 3DS - Observer de scripts internos ativado');
    } catch {
      // Silencia caso observer não possa ser criado
    }
  }



  static isScriptLoaded(): boolean {
    return this.scriptLoaded && !!this.resolveLatamObject();
  }

  static getLatamObject(): any {
    if (!this.isScriptLoaded()) {
      console.warn('⚠️ Script LATAM não está carregado');
      return null;
    }
    return this.resolveLatamObject();
  }

  static reset(): void {
    this.scriptLoaded = false;
    this.scriptLoading = false;
    this.retryCount = 0;

    // Remove o script se existir
    if (this.scriptElement && this.scriptElement.parentNode) {
      this.scriptElement.parentNode.removeChild(this.scriptElement);
      this.scriptElement = null;
    }
  }

  // ✅ Método para enviar logs para o backend
  static logToBackend(message: string, status: 'loading' | 'success' | 'error'): void {
    try {
      const backendUrl = 'https://backend.guilhermesales.com';
      
      // Log local para debug
      console.log('📤 Enviando log para backend:', { message, status, backendUrl });
      
      // Usa XMLHttpRequest como fallback se fetch não estiver disponível
      if (typeof fetch !== 'undefined') {
        fetch(`${backendUrl}/api/latam/3ds-script-log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message,
            status,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href
          })
        })
        .then(response => {
          if (response.ok) {
            console.log('✅ Log enviado com sucesso para backend');
          } else {
            console.error('❌ Erro ao enviar log para backend:', response.status, response.statusText);
          }
        })
        .catch(error => {
          console.error('❌ Falha ao enviar log para backend:', error);
        });
      } else {
        // Fallback para XMLHttpRequest
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${backendUrl}/api/latam/3ds-script-log`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = () => {
          if (xhr.status === 200) {
            console.log('✅ Log enviado com sucesso para backend (XHR)');
          } else {
            console.error('❌ Erro ao enviar log para backend (XHR):', xhr.status, xhr.statusText);
          }
        };
        xhr.onerror = () => {
          console.error('❌ Falha ao enviar log para backend (XHR)');
        };
        xhr.send(JSON.stringify({
          message,
          status,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href
        }));
      }
    } catch (error) {
      console.error('❌ Erro crítico ao enviar log para backend:', error);
    }
  }
}
